import type * as React from 'react'
import { forwardRef, useMemo } from 'react'
import { easeOut, LayoutGroup, motion, MotionConfig } from 'motion/react'

import { splitToSegments } from './utils/splitToSegments'
import { NumberSection } from './NumberSection'
import type { AnimationConfig } from './types'
import { useIsFirstRender } from '../../hooks/useIsFirstRender'
import { WholeNumber } from './WholeNumber'
import { Mask } from './Mask'

/**
 * Default animation configuration for number transitions
 */
export const DEFAULT_TRANSITION: AnimationConfig = {
  opacity: { duration: 1, ease: easeOut },
  layout: { type: 'spring', duration: 1, bounce: 0 },
  y: { type: 'spring', duration: 1, bounce: 0 },
}

type AnimatedNumberProps = {
  /** The number to animate */
  value: number | bigint | string
  /** Whether to animate the whole number as one unit */
  animateAsWhole?: boolean
  /** Custom animation configuration */
  animationConfig?: Partial<AnimationConfig>
}

/**
 * A component that animates number changes with smooth transitions
 * Uses FLIP animation technique for optimal performance
 */
export const AnimatedNumber = forwardRef<
  HTMLDivElement,
  AnimatedNumberProps & { style?: React.CSSProperties }
>(
  (
    { value, style, animationConfig = {}, animateAsWhole = false, ...rest },
    ref,
  ): JSX.Element => {
    const { isFirstRender } = useIsFirstRender()
    const { digits, prefix } = splitToSegments(value)

    // Memoize dependency object to prevent unnecessary re-renders in motion components
    const dependency = useMemo(() => ({ value }), [value])

    // Merge default transition with custom config
    const transition = useMemo(
      () => ({
        ...DEFAULT_TRANSITION,
        ...animationConfig,
      }),
      [animationConfig],
    )

    return (
      <LayoutGroup>
        <MotionConfig transition={transition}>
          <motion.div
            {...rest}
            ref={ref}
            layout
            layoutDependency={dependency}
            role="status"
            aria-live="polite"
            style={{
              lineHeight: 1,
              ...style,
              display: 'inline-flex',
              isolation: 'isolate',
              whiteSpace: 'nowrap',
            }}
          >
            <motion.div
              layout
              layoutDependency={dependency}
              style={{
                display: 'inline-flex',
                direction: 'ltr',
                isolation: 'isolate',
                position: 'relative',
                zIndex: -1,
              }}
            >
              {animateAsWhole ? (
                <Mask layoutDependency={dependency}>
                  <WholeNumber
                    layoutDependency={dependency}
                    value={Number(value)}
                    initialValue={isFirstRender ? undefined : 0}
                  />
                </Mask>
              ) : (
                <>
                  <NumberSection
                    layoutDependency={dependency}
                    justify="right"
                    segments={prefix}
                    name="prefix"
                    mode="popLayout"
                  />
                  <Mask layoutDependency={dependency}>
                    <NumberSection
                      layoutDependency={dependency}
                      justify="right"
                      segments={digits}
                      name="digits"
                    />
                  </Mask>
                </>
              )}
            </motion.div>
          </motion.div>
        </MotionConfig>
      </LayoutGroup>
    )
  },
)

AnimatedNumber.displayName = 'AnimatedNumber'
